import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Trash2, Upload, LogOut, Film, Image as ImageIcon, ArrowLeft, ArrowUp, ArrowDown,
  ArrowLeftRight, Star, Eye, GripVertical, Plus, X, Check,
} from 'lucide-react';
import { MarkdownField } from '../components/Markdown';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CHUNK = 8 * 1024 * 1024;
const CATEGORIES = ['fotografia', 'video', 'marketing', 'websites', 'souvenir'];
const CAT_LABELS = { fotografia: 'Fotografia', video: 'Vídeo', marketing: 'Marketing', websites: 'Websites', souvenir: 'Souvenir' };
const STATUS = {
  draft: { label: 'Rascunho', cls: 'border-zinc-700 text-zinc-400' },
  published: { label: 'Publicado', cls: 'border-fl-yellow text-fl-yellow' },
  hidden: { label: 'Oculto', cls: 'border-zinc-700 text-zinc-600' },
};

function formatSize(bytes) {
  if (!bytes) return '—';
  if (bytes > 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${Math.round(bytes / 1024)} KB`;
}

const inputCls =
  'w-full border border-zinc-800 bg-black px-4 py-3 text-sm text-white placeholder-zinc-700 outline-none focus:border-fl-yellow';

function Field({ label, children, testId }) {
  return (
    <div data-testid={testId}>
      <p className="mb-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{label}</p>
      {children}
    </div>
  );
}

function LangInput({ label, value, onChange, testId, textarea = false }) {
  return (
    <div className="grid gap-3 sm:grid-cols-2" data-testid={testId}>
      {['pt', 'en'].map((l) => (
        <Field key={l} label={`${label} · ${l.toUpperCase()}${l === 'en' ? ' (opcional)' : ''}`}>
          {textarea ? (
            <textarea rows={2} value={value[l] || ''} onChange={(e) => onChange({ ...value, [l]: e.target.value })} className={`${inputCls} resize-y`} data-testid={`${testId}-${l}`} />
          ) : (
            <input value={value[l] || ''} onChange={(e) => onChange({ ...value, [l]: e.target.value })} className={inputCls} data-testid={`${testId}-${l}`} />
          )}
        </Field>
      ))}
    </div>
  );
}

function LangMarkdown({ label, value, onChange, testId }) {
  return (
    <div className="grid gap-4 lg:grid-cols-2" data-testid={testId}>
      {['pt', 'en'].map((l) => (
        <MarkdownField
          key={l}
          label={`${label} · ${l.toUpperCase()}${l === 'en' ? ' (opcional)' : ''}`}
          value={value[l] || ''}
          onChange={(v) => onChange({ ...value, [l]: v })}
          testId={`${testId}-${l}`}
        />
      ))}
    </div>
  );
}

function ServicesEditor({ value, onChange }) {
  const set = (i, l, v) => onChange(value.map((s, j) => (j === i ? { ...s, [l]: v } : s)));
  return (
    <Field label="Serviços realizados" testId="editor-services">
      <div className="space-y-2">
        {value.map((s, i) => (
          <div key={i} className="flex gap-2" data-testid={`editor-service-${i}`}>
            <input value={s.pt} onChange={(e) => set(i, 'pt', e.target.value)} placeholder="PT" className={inputCls} data-testid={`editor-service-${i}-pt`} />
            <input value={s.en} onChange={(e) => set(i, 'en', e.target.value)} placeholder="EN (opcional)" className={inputCls} data-testid={`editor-service-${i}-en`} />
            <button type="button" onClick={() => onChange(value.filter((_, j) => j !== i))} aria-label="Remover serviço" className="shrink-0 border border-zinc-800 px-3 text-zinc-500 hover:text-red-400">
              <X className="h-4 w-4" />
            </button>
          </div>
        ))}
        <button type="button" onClick={() => onChange([...value, { pt: '', en: '' }])} data-testid="editor-service-add" className="flex items-center gap-2 border border-dashed border-zinc-700 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400 hover:border-fl-yellow hover:text-fl-yellow">
          <Plus className="h-3.5 w-3.5" /> Adicionar serviço
        </button>
      </div>
    </Field>
  );
}

function MetricsEditor({ value, onChange }) {
  const set = (i, k, v) => onChange(value.map((m, j) => (j === i ? { ...m, [k]: v } : m)));
  return (
    <Field label="Resultados / métricas (só aparecem se preenchidos)" testId="editor-metrics">
      <div className="space-y-2">
        {value.map((m, i) => (
          <div key={i} className="grid grid-cols-[110px_1fr_1fr_auto] gap-2" data-testid={`editor-metric-${i}`}>
            <input value={m.value} onChange={(e) => set(i, 'value', e.target.value)} placeholder="+120%" className={inputCls} data-testid={`editor-metric-${i}-value`} />
            <input value={m.label?.pt || ''} onChange={(e) => set(i, 'label', { ...m.label, pt: e.target.value })} placeholder="Rótulo PT" className={inputCls} />
            <input value={m.label?.en || ''} onChange={(e) => set(i, 'label', { ...m.label, en: e.target.value })} placeholder="Label EN" className={inputCls} />
            <button type="button" onClick={() => onChange(value.filter((_, j) => j !== i))} aria-label="Remover métrica" className="border border-zinc-800 px-3 text-zinc-500 hover:text-red-400">
              <X className="h-4 w-4" />
            </button>
          </div>
        ))}
        <button type="button" onClick={() => onChange([...value, { value: '', label: { pt: '', en: '' } }])} data-testid="editor-metric-add" className="flex items-center gap-2 border border-dashed border-zinc-700 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400 hover:border-fl-yellow hover:text-fl-yellow">
          <Plus className="h-3.5 w-3.5" /> Adicionar métrica
        </button>
      </div>
    </Field>
  );
}

export default function AdminPage() {
  const [me, setMe] = useState(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [busy, setBusy] = useState(false);

  const token = () => localStorage.getItem('fl_admin_token') || '';
  const authFetch = useCallback((url, opts = {}) => {
    const headers = { ...(opts.headers || {}), Authorization: `Bearer ${token()}` };
    return fetch(url, { ...opts, headers });
  }, []);

  useEffect(() => {
    document.title = 'Admin — F&L Group';
    document.documentElement.style.setProperty('--brand', '#FFCE00');
    (async () => {
      if (!token()) {
        setMe(false);
        return;
      }
      const res = await authFetch(`${API}/auth/me`);
      if (res.ok) setMe(await res.json());
      else {
        localStorage.removeItem('fl_admin_token');
        setMe(false);
      }
    })();
  }, [authFetch]);

  const login = async (e) => {
    e.preventDefault();
    setBusy(true);
    setLoginError('');
    try {
      const res = await fetch(`${API}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof data.detail === 'string' ? data.detail : 'Erro ao entrar');
      localStorage.setItem('fl_admin_token', data.token);
      setMe({ email: data.email });
    } catch (err) {
      setLoginError(err.message);
    }
    setBusy(false);
  };

  const logout = () => {
    localStorage.removeItem('fl_admin_token');
    setMe(false);
  };

  if (me === null) return <div className="flex min-h-screen items-center justify-center bg-black" data-testid="admin-loading" />;

  if (me === false) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-black px-5" data-testid="admin-login-page">
        <form onSubmit={login} data-testid="admin-login-form" className="w-full max-w-sm border border-zinc-800 bg-zinc-950 p-8">
          <p className="font-heading text-xl font-black uppercase tracking-tight text-white">
            F&L <span className="text-fl-yellow">Admin</span>
          </p>
          <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">Gestão do portfólio</p>
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" data-testid="admin-email" className="mt-8 w-full border border-zinc-800 bg-black px-4 py-3 text-sm text-white placeholder-zinc-600 outline-none focus:border-fl-yellow" />
          <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" data-testid="admin-password" className="mt-3 w-full border border-zinc-800 bg-black px-4 py-3 text-sm text-white placeholder-zinc-600 outline-none focus:border-fl-yellow" />
          {loginError && (
            <p className="mt-3 text-xs text-red-400" data-testid="admin-login-error">{loginError}</p>
          )}
          <button type="submit" disabled={busy} data-testid="admin-login-submit" className="mt-6 w-full bg-fl-yellow py-3 font-mono text-xs font-bold uppercase tracking-[0.25em] text-black transition-opacity disabled:opacity-40">
            Entrar
          </button>
        </form>
      </div>
    );
  }

  return <ProjectsAdmin authFetch={authFetch} me={me} onLogout={logout} />;
}

// ---------------- Gestão de projetos ----------------

function ProjectsAdmin({ authFetch, me, onLogout }) {
  const [projects, setProjects] = useState([]);
  const [sortMode, setSortMode] = useState('manual');
  const [brandFilter, setBrandFilter] = useState('all');
  const [editing, setEditing] = useState(null);
  const [newName, setNewName] = useState('');
  const [newBrand, setNewBrand] = useState('media');
  const [creating, setCreating] = useState(false);
  const [status, setStatus] = useState('');
  const dragIdx = useRef(null);

  const load = useCallback(async () => {
    const res = await authFetch(`${API}/admin/projects`);
    if (res.ok) setProjects((await res.json()).projects);
  }, [authFetch]);

  useEffect(() => {
    load();
  }, [load]);

  const create = async (e) => {
    e.preventDefault();
    if (!newName.trim() || creating) return;
    setCreating(true);
    const res = await authFetch(`${API}/admin/projects`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name_pt: newName.trim(), brand: newBrand }),
    });
    const data = await res.json().catch(() => ({}));
    setCreating(false);
    if (res.ok) {
      setNewName('');
      setEditing(data.id);
    } else {
      setStatus(typeof data.detail === 'string' ? data.detail : 'Erro ao criar projeto');
    }
  };

  const persistOrder = async (ids) => {
    const res = await authFetch(`${API}/admin/projects/reorder`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ids }),
    });
    if (res.ok) {
      setStatus('Ordem guardada');
      load();
    } else setStatus('Erro ao guardar a ordem');
  };

  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= projects.length) return;
    const next = [...projects];
    [next[i], next[j]] = [next[j], next[i]];
    setProjects(next);
    persistOrder(next.map((p) => p.id));
  };

  const onDropRow = (i) => {
    const from = dragIdx.current;
    dragIdx.current = null;
    if (from === null || from === i) return;
    const next = [...projects];
    const [moved] = next.splice(from, 1);
    next.splice(i, 0, moved);
    setProjects(next);
    persistOrder(next.map((p) => p.id));
  };

  const toggleFeatured = async (p) => {
    await authFetch(`${API}/admin/projects/${p.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ featured: !p.featured }),
    });
    load();
  };

  const removeProject = async (p) => {
    if (!window.confirm(`Eliminar o projeto "${p.name.pt}"? As fotografias e vídeos associados também deixam de aparecer.`)) return;
    const res = await authFetch(`${API}/admin/projects/${p.id}/delete`, { method: 'POST' });
    if (res.ok) load();
    else setStatus('Erro ao eliminar o projeto');
  };

  const filtered = brandFilter === 'all' ? projects : projects.filter((p) => p.brand === brandFilter);
  const canOrder = sortMode === 'manual' && brandFilter === 'all';
  const shown = sortMode === 'date' ? [...filtered].sort((a, b) => (b.created_at || '').localeCompare(a.created_at || '')) : filtered;

  if (editing) {
    return (
      <ProjectEditor
        authFetch={authFetch}
        projectId={editing}
        onBack={() => {
          setEditing(null);
          load();
        }}
      />
    );
  }

  return (
    <div className="min-h-screen bg-black px-5 py-10 text-white sm:px-10" data-testid="admin-dashboard">
      <div className="mx-auto max-w-5xl">
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-zinc-800 pb-6">
          <div>
            <p className="font-heading text-2xl font-black uppercase tracking-tight">
              F&L <span className="text-fl-yellow">Admin</span>
            </p>
            <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">Gestão do portfólio · {me.email}</p>
          </div>
          <button onClick={onLogout} data-testid="admin-logout" className="flex items-center gap-2 border border-zinc-800 px-4 py-2 font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-400 transition-colors hover:border-fl-yellow hover:text-fl-yellow">
            <LogOut className="h-3.5 w-3.5" /> Sair
          </button>
        </div>

        <form onSubmit={create} className="mt-8 flex flex-wrap items-center gap-3 border border-zinc-800 bg-zinc-950 p-4" data-testid="admin-new-project">
          <input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Nome do novo projeto" data-testid="admin-new-name" className="min-w-52 flex-1 border border-zinc-800 bg-black px-4 py-3 text-sm text-white placeholder-zinc-700 outline-none focus:border-fl-yellow" />
          <select value={newBrand} onChange={(e) => setNewBrand(e.target.value)} data-testid="admin-new-brand" className="border border-zinc-800 bg-black px-4 py-3 text-sm text-white outline-none focus:border-fl-yellow">
            <option value="media">F&L Media</option>
            <option value="souvenir">F&L Souvenir</option>
          </select>
          <button type="submit" disabled={creating || !newName.trim()} data-testid="admin-new-create" className="flex items-center gap-2 bg-fl-yellow px-6 py-3 font-mono text-xs font-bold uppercase tracking-[0.25em] text-black disabled:opacity-40">
            <Plus className="h-4 w-4" /> Adicionar projeto
          </button>
        </form>

        <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
            {shown.length} de {projects.length} projeto(s) · arrasta ou usa as setas para ordenar
          </p>
          <div className="flex flex-wrap gap-2">
            <div className="flex gap-2" data-testid="admin-brand-filter">
              {[['all', 'Todas'], ['media', 'F&L Media'], ['souvenir', 'F&L Souvenir']].map(([b, label]) => (
                <button key={b} onClick={() => setBrandFilter(b)} data-testid={`admin-filter-${b}`} className={`border px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors ${brandFilter === b ? 'border-fl-yellow text-fl-yellow' : 'border-zinc-800 text-zinc-500 hover:text-white'}`}>
                  {label}
                </button>
              ))}
            </div>
            <div className="flex gap-2" data-testid="admin-sort-toggle">
              {['manual', 'date'].map((m2) => (
                <button key={m2} onClick={() => setSortMode(m2)} data-testid={`admin-sort-${m2}`} className={`border px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors ${sortMode === m2 ? 'border-fl-yellow text-fl-yellow' : 'border-zinc-800 text-zinc-500 hover:text-white'}`}>
                  {m2 === 'manual' ? 'Ordem manual' : 'Por data'}
                </button>
              ))}
            </div>
          </div>
        </div>
        {status && (
          <p className="mt-3 font-mono text-[11px] uppercase tracking-[0.2em] text-fl-yellow" data-testid="admin-status">{status}</p>
        )}

        <div className="mt-4 space-y-3" data-testid="admin-projects-list">
          {shown.map((p, i) => (
            <div
              key={p.id}
              draggable={canOrder}
              onDragStart={() => (dragIdx.current = i)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => onDropRow(i)}
              className="flex items-center gap-4 border border-zinc-800 bg-zinc-950 p-3 sm:p-4"
              data-testid={`admin-project-${p.id}`}
            >
              {canOrder && (
                <div className="flex items-center gap-1 text-zinc-600">
                  <GripVertical className="hidden h-4 w-4 cursor-grab sm:block" />
                  <div className="flex flex-col">
                    <button onClick={() => move(i, -1)} disabled={i === 0} aria-label="Subir" data-testid={`admin-project-up-${i}`} className="hover:text-fl-yellow disabled:opacity-20">
                      <ArrowUp className="h-4 w-4" />
                    </button>
                    <button onClick={() => move(i, 1)} disabled={i === shown.length - 1} aria-label="Descer" data-testid={`admin-project-down-${i}`} className="hover:text-fl-yellow disabled:opacity-20">
                      <ArrowDown className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              )}
              <div className="h-14 w-20 shrink-0 overflow-hidden bg-zinc-900">
                {p.cover_thumb ? (
                  <img src={`${process.env.REACT_APP_BACKEND_URL}${p.cover_thumb}`} alt="" className="h-full w-full object-cover" />
                ) : (
                  <div className="flex h-full items-center justify-center text-zinc-700">
                    <ImageIcon className="h-5 w-5" />
                  </div>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-bold">{p.name.pt}</p>
                <p className="mt-1 flex flex-wrap items-center gap-2 font-mono text-[9px] uppercase tracking-[0.15em] text-zinc-500">
                  <span>{p.brand === 'media' ? 'F&L Media' : 'F&L Souvenir'}</span>
                  <span>·</span>
                  <span>{p.media_count} ficheiro(s)</span>
                  {p.categories.map((c) => (
                    <span key={c} className="border border-zinc-800 px-1.5 py-0.5">{CAT_LABELS[c]}</span>
                  ))}
                </p>
              </div>
              <span className={`hidden border px-2 py-1 font-mono text-[9px] uppercase tracking-[0.2em] sm:inline-block ${STATUS[p.status]?.cls || ''}`} data-testid={`admin-project-status-${p.id}`}>
                {STATUS[p.status]?.label}
              </span>
              <button onClick={() => toggleFeatured(p)} aria-label="Destacar" title="Projeto em destaque (mostrar primeiro)" data-testid={`admin-project-star-${p.id}`} className={`transition-colors ${p.featured ? 'text-fl-yellow' : 'text-zinc-600 hover:text-fl-yellow'}`}>
                <Star className="h-5 w-5" fill={p.featured ? 'currentColor' : 'none'} />
              </button>
              <button onClick={() => setEditing(p.id)} data-testid={`admin-project-edit-${p.id}`} className="border border-zinc-800 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-white transition-colors hover:border-fl-yellow hover:text-fl-yellow">
                Editar
              </button>
              <button onClick={() => removeProject(p)} aria-label="Eliminar projeto" data-testid={`admin-project-delete-${p.id}`} className="text-zinc-600 transition-colors hover:text-red-400">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
          {shown.length === 0 && (
            <p className="border border-dashed border-zinc-800 p-8 text-center font-mono text-xs uppercase tracking-[0.25em] text-zinc-600">
              {projects.length === 0 ? 'Ainda sem projetos — cria o primeiro acima' : 'Sem projetos desta marca'}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------- Editor de projeto ----------------

const TABS = ['Informações', 'Descrição', 'Galeria', 'Publicação'];

function ProjectEditor({ authFetch, projectId, onBack }) {
  const [p, setP] = useState(null);
  const [tab, setTab] = useState('Informações');
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [uploads, setUploads] = useState([]); // {name, pct, error}
  const fileRef = useRef(null);
  const replaceRef = useRef(null);
  const replaceTarget = useRef(null);
  const dragMedia = useRef(null);
  const [previewImg, setPreviewImg] = useState(null);

  const load = useCallback(async () => {
    const res = await authFetch(`${API}/admin/projects/${projectId}`);
    if (res.ok) {
      setP(await res.json());
      setDirty(false);
    }
  }, [authFetch, projectId]);

  useEffect(() => {
    load();
  }, [load]);

  const set = (k, v) => {
    setP((cur) => ({ ...cur, [k]: v }));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    setMsg('');
    const res = await authFetch(`${API}/admin/projects/${projectId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: p.name, tagline: p.tagline, intro: p.intro, objective: p.objective,
        challenge: p.challenge, what_we_did: p.what_we_did, process: p.process,
        result_text: p.result_text, client: p.client || '', year: p.year || '',
        location: p.location || '', categories: p.categories, services: p.services,
        metrics: p.metrics, status: p.status, featured: p.featured,
      }),
    });
    setSaving(false);
    if (res.ok) {
      setDirty(false);
      setMsg('Alterações guardadas');
    } else {
      const d = await res.json().catch(() => ({}));
      setMsg(typeof d.detail === 'string' ? d.detail : 'Erro ao guardar');
    }
  };

  const uploadChunk = (uploadId, index, blob, fileSize, upIdx, total) =>
    new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${API}/admin/media/chunk?upload_id=${uploadId}&index=${index}`);
      xhr.setRequestHeader('Authorization', `Bearer ${localStorage.getItem('fl_admin_token') || ''}`);
      xhr.upload.onprogress = (ev) => {
        if (ev.lengthComputable) {
          const done = index * CHUNK + ev.loaded;
          setUploads((cur) => cur.map((u, j) => (j === upIdx ? { ...u, pct: Math.min(99, Math.round((done / fileSize) * 100)) } : u)));
        }
      };
      xhr.onload = () => (xhr.status < 300 ? resolve() : reject(new Error(`falha (${xhr.status})`)));
      xhr.onerror = () => reject(new Error('falha de rede'));
      xhr.send(blob);
    });

  const uploadFile = async (file, upIdx) => {
    const uploadId = crypto.randomUUID();
    const isVideo = file.type.startsWith('video/');
    const total = Math.ceil(file.size / CHUNK);
    for (let i = 0; i < total; i++) {
      await uploadChunk(uploadId, i, file.slice(i * CHUNK, (i + 1) * CHUNK), file.size, upIdx, total);
    }
    const res = await authFetch(`${API}/admin/media/complete`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        upload_id: uploadId,
        total_chunks: total,
        filename: file.name,
        brand: p.brand,
        media_type: isVideo ? 'video' : 'photo',
        project_id: projectId,
      }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(typeof data.detail === 'string' ? data.detail : 'erro ao concluir');
    return data;
  };

  const onFiles = async (files) => {
    const list = Array.from(files || []).filter((f) => f.type.startsWith('image/') || f.type.startsWith('video/'));
    if (!list.length) return;
    const base = uploads.length;
    setUploads((cur) => [...cur, ...list.map((f) => { const u = { name: f.name, pct: 0, error: '' }; u.base = base; return u; })]);
    for (let i = 0; i < list.length; i++) {
      try {
        await uploadFile(list[i], base + i);
        setUploads((cur) => cur.map((u, j) => (j === base + i ? { ...u, pct: 100 } : u)));
      } catch (err) {
        setUploads((cur) => cur.map((u, j) => (j === base + i ? { ...u, error: err.message } : u)));
      }
    }
    await load();
    setTimeout(() => setUploads((cur) => cur.filter((u) => u.error)), 4000);
    setTimeout(() => setUploads([]), 4000);
  };

  const mediaIds = () => p.media.map((m) => m.id);

  const persistMediaOrder = async (ids) => {
    const res = await authFetch(`${API}/admin/projects/${projectId}/media/reorder`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ids }),
    });
    if (res.ok) {
      setMsg('Ordem da galeria guardada');
      load();
    } else setMsg('Erro ao guardar a ordem');
  };

  const moveMedia = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= p.media.length) return;
    const ids = mediaIds();
    [ids[i], ids[j]] = [ids[j], ids[i]];
    persistMediaOrder(ids);
  };

  const onDropMedia = (i) => {
    const from = dragMedia.current;
    dragMedia.current = null;
    if (from === null || from === i) return;
    const ids = mediaIds();
    const [moved] = ids.splice(from, 1);
    ids.splice(i, 0, moved);
    persistMediaOrder(ids);
  };

  const setCover = async (mid) => {
    await authFetch(`${API}/admin/projects/${projectId}/media/cover`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ media_id: mid }),
    });
    setMsg('Capa atualizada');
    load();
  };

  const deleteMedia = async (mid) => {
    if (!window.confirm('Eliminar este ficheiro da galeria?')) return;
    const res = await authFetch(`${API}/admin/media/${mid}/delete`, { method: 'POST' });
    if (res.ok) load();
    else setMsg('Erro ao eliminar o ficheiro');
  };

  const onReplaceFile = async (file) => {
    const oldId = replaceTarget.current;
    replaceTarget.current = null;
    if (!file || !oldId) return;
    setMsg('A substituir ficheiro...');
    try {
      setUploads([{ name: file.name, pct: 0, error: '' }]);
      const data = await uploadFile(file, 0);
      const ids = mediaIds().map((id) => (id === oldId ? data.id : id));
      await authFetch(`${API}/admin/projects/${projectId}/media/reorder`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids }),
      });
      await authFetch(`${API}/admin/media/${oldId}/delete`, { method: 'POST' });
      setMsg('Ficheiro substituído');
    } catch (err) {
      setMsg(`Erro ao substituir: ${err.message}`);
    }
    setUploads([]);
    load();
  };

  const deleteProject = async () => {
    if (!window.confirm(`Eliminar o projeto "${p.name.pt}"? Esta ação remove-o do website.`)) return;
    const res = await authFetch(`${API}/admin/projects/${projectId}/delete`, { method: 'POST' });
    if (res.ok) onBack();
    else setMsg('Erro ao eliminar o projeto');
  };

  if (!p) return <div className="min-h-screen bg-black" data-testid="editor-loading" />;

  const previewUrl = `/pt/portfolio/${p.slug}?preview=${p.id}`;

  return (
    <div className="min-h-screen bg-black px-5 py-10 text-white sm:px-10" data-testid="project-editor">
      <div className="mx-auto max-w-5xl">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-zinc-800 pb-5">
          <button onClick={onBack} data-testid="editor-back" className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-400 transition-colors hover:text-fl-yellow">
            <ArrowLeft className="h-4 w-4" /> Projetos
          </button>
          <div className="flex items-center gap-3">
            <span className={`border px-2 py-1 font-mono text-[9px] uppercase tracking-[0.2em] ${STATUS[p.status]?.cls || ''}`} data-testid="editor-status-badge">
              {STATUS[p.status]?.label}
            </span>
            <a href={previewUrl} target="_blank" rel="noopener noreferrer" data-testid="editor-preview" className="flex items-center gap-2 border border-zinc-800 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-white transition-colors hover:border-fl-yellow hover:text-fl-yellow">
              <Eye className="h-3.5 w-3.5" /> Pré-visualizar projeto
            </a>
          </div>
        </div>

        <div className="mt-6 flex flex-wrap gap-2" data-testid="editor-tabs">
          {TABS.map((tb) => (
            <button key={tb} onClick={() => setTab(tb)} data-testid={`editor-tab-${tb.toLowerCase()}`} className={`border px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors ${tab === tb ? 'border-fl-yellow bg-fl-yellow text-black' : 'border-zinc-800 text-zinc-400 hover:text-white'}`}>
              {tb}
            </button>
          ))}
        </div>

        <div className="mt-8 space-y-6">
          {tab === 'Informações' && (
            <>
              <LangInput label="Nome do projeto" value={p.name} onChange={(v) => set('name', v)} testId="editor-name" />
              <div className="grid gap-4 sm:grid-cols-3">
                <Field label="Cliente (opcional)" testId="editor-client">
                  <input value={p.client || ''} onChange={(e) => set('client', e.target.value)} className={inputCls} data-testid="editor-client-input" />
                </Field>
                <Field label="Ano (opcional)" testId="editor-year">
                  <input value={p.year || ''} onChange={(e) => set('year', e.target.value)} placeholder="2026" className={inputCls} data-testid="editor-year-input" />
                </Field>
                <Field label="Localização (opcional)" testId="editor-location">
                  <input value={p.location || ''} onChange={(e) => set('location', e.target.value)} className={inputCls} data-testid="editor-location-input" />
                </Field>
              </div>
              <Field label="Categorias" testId="editor-categories">
                <div className="flex flex-wrap gap-2">
                  {CATEGORIES.map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => set('categories', p.categories.includes(c) ? p.categories.filter((x) => x !== c) : [...p.categories, c])}
                      data-testid={`editor-cat-${c}`}
                      aria-pressed={p.categories.includes(c)}
                      className={`flex items-center gap-1.5 border px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors ${p.categories.includes(c) ? 'border-fl-yellow bg-fl-yellow text-black' : 'border-zinc-800 text-zinc-400 hover:text-white'}`}
                    >
                      {p.categories.includes(c) && <Check className="h-3 w-3" />}
                      {CAT_LABELS[c]}
                    </button>
                  ))}
                </div>
              </Field>
              <LangInput label="Frase curta (cartão do portfólio)" value={p.tagline} onChange={(v) => set('tagline', v)} testId="editor-tagline" textarea />
            </>
          )}

          {tab === 'Descrição' && (
            <>
              <LangMarkdown label="Introdução e contexto" value={p.intro} onChange={(v) => set('intro', v)} testId="editor-intro" />
              <LangMarkdown label="Objetivo do projeto" value={p.objective} onChange={(v) => set('objective', v)} testId="editor-objective" />
              <LangMarkdown label="O desafio" value={p.challenge} onChange={(v) => set('challenge', v)} testId="editor-challenge" />
              <LangMarkdown label="O que fizemos" value={p.what_we_did} onChange={(v) => set('what_we_did', v)} testId="editor-whatwedid" />
              <LangMarkdown label="Processo criativo / produção" value={p.process} onChange={(v) => set('process', v)} testId="editor-process" />
              <LangMarkdown label="Resultado final" value={p.result_text} onChange={(v) => set('result_text', v)} testId="editor-result" />
              <ServicesEditor value={p.services} onChange={(v) => set('services', v)} />
              <MetricsEditor value={p.metrics} onChange={(v) => set('metrics', v)} />
            </>
          )}

          {tab === 'Galeria' && (
            <>
              <label className="flex cursor-pointer flex-col items-center justify-center gap-2 border border-dashed border-zinc-700 p-8 text-center transition-colors hover:border-fl-yellow" data-testid="editor-upload-drop">
                <Upload className="h-5 w-5 text-zinc-500" />
                <span className="text-sm text-zinc-400">Adicionar fotografias e/ou vídeos (podes escolher vários de uma vez)</span>
                <input
                  ref={fileRef}
                  type="file"
                  multiple
                  accept="image/jpeg,image/png,image/webp,video/mp4,video/webm,video/quicktime"
                  className="hidden"
                  data-testid="editor-file-input"
                  onChange={(e) => {
                    onFiles(e.target.files);
                    e.target.value = '';
                  }}
                />
              </label>
              {uploads.length > 0 && (
                <div className="space-y-2" data-testid="editor-uploads">
                  {uploads.map((u, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="h-1 flex-1 bg-zinc-900">
                        <div className="h-1 bg-fl-yellow transition-all" style={{ width: `${u.pct}%` }} />
                      </div>
                      <span className="w-40 truncate font-mono text-[10px] text-zinc-500">{u.name}</span>
                      <span className="font-mono text-[10px] text-zinc-400">{u.error ? <span className="text-red-400">{u.error}</span> : `${u.pct}%`}</span>
                    </div>
                  ))}
                </div>
              )}
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                Arrasta para reordenar (guarda automaticamente) · estrela define a capa
              </p>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4" data-testid="editor-media-grid">
                {p.media.map((m, i) => (
                  <div
                    key={m.id}
                    draggable
                    onDragStart={() => (dragMedia.current = i)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => onDropMedia(i)}
                    className={`group relative border bg-zinc-950 ${m.is_cover ? 'border-fl-yellow' : 'border-zinc-800'}`}
                    data-testid={`editor-media-${m.id}`}
                  >
                    <button type="button" onClick={() => setPreviewImg(m)} className="block aspect-square w-full overflow-hidden bg-zinc-900" data-testid={`editor-media-preview-${m.id}`}>
                      {m.media_type === 'photo' ? (
                        <img src={`${process.env.REACT_APP_BACKEND_URL}${m.thumb}`} alt="" loading="lazy" className="h-full w-full object-cover" />
                      ) : (
                        <div className="relative h-full w-full">
                          {m.poster ? (
                            <img src={`${process.env.REACT_APP_BACKEND_URL}${m.poster}`} alt="" loading="lazy" className="h-full w-full object-cover opacity-70" />
                          ) : null}
                          <Film className="absolute left-1/2 top-1/2 h-6 w-6 -translate-x-1/2 -translate-y-1/2 text-white" />
                        </div>
                      )}
                    </button>
                    {m.is_cover && (
                      <span className="absolute left-1.5 top-1.5 bg-fl-yellow px-1.5 py-0.5 font-mono text-[8px] font-bold uppercase tracking-[0.15em] text-black">Capa</span>
                    )}
                    <div className="flex items-center justify-between px-1 py-1">
                      <span className="flex">
                        <button onClick={() => moveMedia(i, -1)} disabled={i === 0} aria-label="Mover para trás" data-testid={`editor-media-left-${m.id}`} className="p-1 text-zinc-500 hover:text-fl-yellow disabled:opacity-20">
                          <ArrowLeft className="h-3.5 w-3.5" />
                        </button>
                        <button onClick={() => moveMedia(i, 1)} disabled={i === p.media.length - 1} aria-label="Mover para a frente" data-testid={`editor-media-right-${m.id}`} className="p-1 text-zinc-500 hover:text-fl-yellow disabled:opacity-20">
                          <ArrowLeft className="h-3.5 w-3.5 rotate-180" />
                        </button>
                      </span>
                      <span className="flex">
                        {m.media_type === 'photo' && (
                          <button onClick={() => setCover(m.id)} aria-label="Definir como capa" title="Definir como capa" data-testid={`editor-media-cover-${m.id}`} className={`p-1 ${m.is_cover ? 'text-fl-yellow' : 'text-zinc-500 hover:text-fl-yellow'}`}>
                            <Star className="h-3.5 w-3.5" fill={m.is_cover ? 'currentColor' : 'none'} />
                          </button>
                        )}
                        <button
                          onClick={() => {
                            replaceTarget.current = m.id;
                            replaceRef.current?.click();
                          }}
                          aria-label="Substituir"
                          title="Substituir ficheiro"
                          data-testid={`editor-media-replace-${m.id}`}
                          className="p-1 text-zinc-500 hover:text-white"
                        >
                          <ArrowLeftRight className="h-3.5 w-3.5" />
                        </button>
                        <button onClick={() => deleteMedia(m.id)} aria-label="Eliminar" data-testid={`editor-media-delete-${m.id}`} className="p-1 text-zinc-500 hover:text-red-400">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </span>
                    </div>
                  </div>
                ))}
              </div>
              {p.media.length === 0 && (
                <p className="border border-dashed border-zinc-800 p-8 text-center font-mono text-xs uppercase tracking-[0.25em] text-zinc-600">
                  Sem ficheiros — carrega a primeira fotografia ou vídeo
                </p>
              )}
              <input
                ref={replaceRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,video/mp4,video/webm,video/quicktime"
                className="hidden"
                data-testid="editor-replace-input"
                onChange={(e) => {
                  onReplaceFile(e.target.files?.[0]);
                  e.target.value = '';
                }}
              />
            </>
          )}

          {tab === 'Publicação' && (
            <>
              <Field label="Estado do projeto" testId="editor-status">
                <div className="grid gap-2 sm:grid-cols-3">
                  {Object.entries(STATUS).map(([key, s]) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => set('status', key)}
                      data-testid={`editor-status-${key}`}
                      aria-pressed={p.status === key}
                      className={`border p-4 text-left transition-colors ${p.status === key ? 'border-fl-yellow' : 'border-zinc-800 hover:border-zinc-600'}`}
                    >
                      <p className={`font-mono text-[11px] uppercase tracking-[0.2em] ${p.status === key ? 'text-fl-yellow' : 'text-zinc-400'}`}>{s.label}</p>
                      <p className="mt-1 text-xs text-zinc-600">
                        {key === 'draft' && 'Guardado no admin, sem aparecer no website.'}
                        {key === 'published' && 'Visível no portfólio público.'}
                        {key === 'hidden' && 'Guardado, mas escondido do website.'}
                      </p>
                    </button>
                  ))}
                </div>
              </Field>
              <label className="flex cursor-pointer items-center gap-3 border border-zinc-800 p-4" data-testid="editor-featured">
                <input type="checkbox" checked={!!p.featured} onChange={(e) => set('featured', e.target.checked)} className="h-4 w-4 accent-[#FFCE00]" data-testid="editor-featured-input" />
                <span className="text-sm text-zinc-300">
                  Projeto em destaque — <span className="text-zinc-500">mostrar primeiro no portfólio</span>
                </span>
              </label>
              <Field label="Endereço público">
                <p className="border border-zinc-800 bg-zinc-950 px-4 py-3 font-mono text-xs text-zinc-400" data-testid="editor-slug">
                  /pt/portfolio/{p.slug}
                </p>
              </Field>
              <button onClick={deleteProject} data-testid="editor-delete-project" className="flex items-center gap-2 border border-red-900 px-5 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-red-400 transition-colors hover:bg-red-950">
                <Trash2 className="h-4 w-4" /> Eliminar projeto
              </button>
            </>
          )}
        </div>

        <div className="sticky bottom-0 mt-10 flex items-center gap-4 border-t border-zinc-800 bg-black/95 py-4 backdrop-blur">
          <button onClick={save} disabled={saving || !dirty} data-testid="editor-save" className="bg-fl-yellow px-8 py-3 font-mono text-xs font-bold uppercase tracking-[0.25em] text-black transition-opacity disabled:opacity-40">
            {saving ? 'A guardar...' : 'Guardar alterações'}
          </button>
          {dirty && <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500" data-testid="editor-dirty">por guardar</span>}
          {msg && <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-fl-yellow" data-testid="editor-msg">{msg}</span>}
        </div>
      </div>

      {previewImg && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/95 p-4" onClick={() => setPreviewImg(null)} data-testid="editor-media-lightbox">
          <button className="absolute right-5 top-5 text-zinc-400 hover:text-white" aria-label="Fechar">
            <X className="h-7 w-7" />
          </button>
          {previewImg.media_type === 'photo' ? (
            <img src={`${process.env.REACT_APP_BACKEND_URL}${previewImg.full}`} alt="" className="max-h-[85vh] max-w-full object-contain" />
          ) : (
            <video controls autoPlay playsInline poster={previewImg.poster ? `${process.env.REACT_APP_BACKEND_URL}${previewImg.poster}` : undefined} className="max-h-[85vh] max-w-full">
              <source src={`${process.env.REACT_APP_BACKEND_URL}${previewImg.full}`} type="video/mp4" />
            </video>
          )}
        </div>
      )}
    </div>
  );
}
